import { useEffect, useMemo, useState } from "react";
import ChartCompareProfileSelectContext from "components/charting/contexts/ChartCompareProfileSelectContext";
import { useChartProfileSelectContext } from "components/charting/contexts/ChartProfileSelectContext";
import LoadVelocityProfileHelpers from "helpers/LoadVelocityProfileHelpers";
import type { LoadVelocityProfile } from "types/load-velocity-profile";

interface ChartCompareProfileSelectProviderProps {
  children?: React.ReactNode;
}

function ChartCompareProfileSelectProvider(props: ChartCompareProfileSelectProviderProps) {
  const { options: baselineProfileOptions } = useChartProfileSelectContext();
  const [selectedProfiles, setSelectedProfiles] = useState<LoadVelocityProfile[]>([]);

  // Only unilateral profiles can be compared, one side against the other.
  const profileOptions = useMemo(
    () => baselineProfileOptions.filter((profile) => profile.variant),
    [baselineProfileOptions]
  );

  // Drop selected profiles once they stop being offered (e.g. on the power profile), so they don't return with them,
  // and keep the rest pointing at the fresh instances a refetch brings. An effect rather than the profile type
  // handler: the offered profiles also change through the URL, the exercise, the athlete and the chart type.
  useEffect(() => {
    setSelectedProfiles((profiles) => LoadVelocityProfileHelpers.reconcile(profiles, profileOptions));
  }, [profileOptions]);

  const contextValue = useMemo(
    () => ({
      value: LoadVelocityProfileHelpers.reconcile(selectedProfiles, profileOptions),
      options: profileOptions,
      onChange: (profiles: LoadVelocityProfile[]) => {
        setSelectedProfiles(profiles);
      },
    }),
    [profileOptions, selectedProfiles]
  );

  return (
    <ChartCompareProfileSelectContext.Provider value={contextValue}>
      {props.children}
    </ChartCompareProfileSelectContext.Provider>
  );
}

export default ChartCompareProfileSelectProvider;
